<template lang="pug">
.author-home.flex.flex-col.h-screen.w-screen.bg-white.dark_bg-gray-700
  .flex.gap-2.p-2.z-50.bg-gray-100.dark_bg-gray-800.h-14.items-center
    MainMenu
      .flex.flex-col
        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          href="#/home"
        )
          | Home – all courses

        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          :href="firmCode ? `#/editor/${firmCode}_${courseId}` : `#/editor/${courseId}`"
        )
          | Edit #[b {{ yamlFilename }}]

        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          :href="firmCode ? `#/history/${firmCode}_${courseId}` : `#/history/${courseId}`"
        )
          | View #[b {{ yamlFilename }}] history
    .ms-auto
    ToggleDark

  .flex-1.p-24.overflow-y-auto.overflow-x-visible
    .max-w-screen-2xl.mx-auto
      .prose.prose.dark_prose-invert.max-w-none
        h1.font-normal 
          span Translate <b>{{ yamlFilename }} </b>

        p.opacity-80 
          span(v-if="title")
            a(:href="`#/editor/${yamlFilename.slice(0, -4)}`") {{ title }} 
            |  
            | &ndash;
          span(v-if="lastUpdated") 
            | Last updated 
            |  
            a(:href="`#/history/${yamlFilename.slice(0, -4)}`") {{ formatTimeAgo(lastUpdated) }}

        template(v-if="loadCourseError")
          p.text-xl.italic 🚨 {{ loadCourseError }}

        template(v-else-if="!lastUpdated")
          p.animate-loading Loading...

        div(v-else-if="isReady")

          //- template(v-if="courseLanguages.length > 0")
          form.p-4.my-6.bg-gray-100.dark_bg-gray-800.bg-opacity-10.rounded-lg.prose.dark_prose-invert(
            v-if="courseLanguages.length > 0"
          )
            h2.mt-0
              span Current course translations: {{ courseLanguages.length }}
              span(v-if="deprecatedCount > 0") &nbsp;({{ deprecatedCount }} deprecated)

            //- select all checkbox
            label.inline-flex.items-center.gap-1.italic.mb-8
              input(
                type="checkbox"
                :id="`langselect_all`"
                @change="selectAllLangs"
                :checked="allSelected"
              )
              span Select All

            .prose.dark_prose-invert
              .grid.grid-cols-4.gap-2
                label.inline-flex.items-center.gap-1(
                  :class="{ 'p-2 bg-red-600/40': isDeprecated(l) }"
                  v-for="l in courseLanguages"
                )
                  input(
                    :id="`langselect_${l}`"
                    type="checkbox"
                    :value="l"
                    @change="updateLangs"
                    :checked="langs.includes(l)"
                  )
                  span {{ languagesMap[l].english }} ({{ l }})
                  abbr.mx-0.-mt-2.no-underline.border-b.border-dotted.scale-75.text-xl.font-bold(title="Deprecated language (Export only).") 
                    span(v-if="isDeprecated(l)") [?]

          .p-4.my-6.bg-gray-100.dark_bg-gray-800.bg-opacity-10.rounded-lg.prose.prose.dark_prose-invert
            h2
              LanguageSelect.text-xl(v-model="langs")

            p.opacity-80 You can type multiple languages separated by spaces, like "de es fr," or click "Add..." to choose from a list.

          fieldset.p-4.my-6.bg-gray-100.dark_bg-gray-800.bg-opacity-10.rounded-lg.prose.prose.dark_prose-invert
            h2 Run translations

            .bg-yellow-800.bg-opacity-20.p-2.px-6(v-if="yaml?.includes('voicemail') || yaml?.includes('guided-voicemail') || yaml?.includes('voicemail-question')")
              p #[span.text-yellow-500.text-2xl ⚠️] <b>Voicemails!</b><br> This course contains voicemails. You will need to translate the audio separately!

            template(v-if="dryRunError")
              p.text-xl.italic 🚨 {{ dryRunError }}
            template(v-else-if="!hasSavedData")
              p.text-xl.italic 🚨 No course found.
            template(v-else-if="!hasBaseDiff")
              p
                LanguageSelect(v-model="langs")

              .flex.gap-6.items-center
                label.flex.gap-1.items-center
                  input(type="checkbox" v-model="generateAi") 
                  |  
                  span Create AI translations

                label.flex.gap-1.items-center
                  input(type="checkbox" v-model="refreshAi") 
                  |  
                  span Refresh existing AI translations
                  abbr.mx-0.no-underline.border-b.border-dotted.font-bold.opacity-50(title="Refresh existing AI translations, even if the English has not changed.") 
                    span [?]

              .flex.gap-6.items-center.mt-4
                label.flex.gap-2.items-center.bg-gray-100.dark_bg-gray-800.bg-opacity-10.rounded-lg.text-gray-900.dark_text-gray-100
                  span Translation Provider:
                  select.rounded.px-2.py-1(v-model="translationProvider")
                    option.text-gray-900.dark_text-gray-100.bg-white.dark_bg-gray-900(value="azure") Azure
                    option.text-gray-900.dark_text-gray-100.bg-white.dark_bg-gray-900(value="openai") OpenAI

              div
                TransitionExpand
                  div(v-if="hasDiff")
                    p.opacity-80 Translating a course may automatically add IDs to the YAML. 

                    p.opacity-80 Running this translation now will save a new version of the YAML with the following changes. You will need to publish the YAML from the editor to make the changes live.

                    p This course needs some IDs for translations.
                    p.not-prose
                      PrettyDiff(:diff="diff" :name="yamlFilename")

                TransitionExpand
                  div(v-if="!hasDiff")
                    p The YAML has all its IDs ready to translate.

                p.opacity-80 After you generate translations, use "Export" below to review them, and "Publish" to make them live.

              p
                button.ms-auto.btn-hover_cursor-pointer.bg-primary-600.btn-hover_bg-primary-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(type="button" :disabled="!langs || !validLangs" @click="translate") 
                  span.group-disabled_opacity-50 Generate
                span.px-4
                  span.italic.opacity-50(v-if="!langs") Please choose some languages.
                  span.italic.opacity-50(v-else-if="!validLangs") You chose an unknown/deprecated language.
                  span.opacity-75(v-else-if="statuses.translate === 'pending'") Working...
                  span.opacity-75(v-else-if="statuses.translate === 'success'") Done!
                  span.text-red-700.dark_text-red-400(v-else-if="statuses.translate === 'error'") {{ errors.translate }}

            template(v-else-if="hasBaseDiff") 
              p.not-prose.border-2.border-dashed.border-red-500.rounded-xl.p-6
                p.font-bold The base course needs some IDs before custom translation can be run.
                p Please translate the base course into your required languages first.
                p.mt-2
                  span
                    a.text-blue-500.hover_underline(:href="`#/translate/${baseCourseYamlFilename.slice(0, -4)}`") Translate {{ baseCourseYamlFilename }}

          fieldset.p-4.my-6.bg-gray-100.dark_bg-gray-800.bg-opacity-10.rounded-lg.prose.prose.dark_prose-invert
            h2 Export translations
            p.opacity-80 Download TSV files containing all phrases of the latest version of the course with their saved translations. If some phrases are not translated, their third column will be empty. If the TSV includes AI translations, the filename will end in ".ai.tsv".

            p.opacity-80 Multiple files will be downloaded as a zip. Tick below to also include video caption files (VTT).

            p
              LanguageSelect(v-model="langs" includeDeprecated=true)

            p
              label.flex.gap-1.items-center
                input(type="checkbox" v-model="shouldIncludeAllTranslations") 
                |  
                span Include ALL translations (all types and archived)

            p
              label.flex.gap-1.items-center
                input(type="checkbox" v-model="shouldExportVideoCaptions") 
                |  
                span Export video caption files (VTT)

            p
              button.ms-auto.btn-hover_cursor-pointer.bg-primary-600.btn-hover_bg-primary-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(type="button" :disabled="!langs || !validExportLangs" @click="download")  
                span.group-disabled_opacity-50 Export

              span.px-4
                span.italic.opacity-50(v-if="!langs") Please choose some languages.
                span.italic.opacity-50(v-else-if="!validExportLangs") You chose an unknown/deprecated language.
                span.opacity-75(v-else-if="statuses.export === 'pending'") Working...
                span.opacity-75(v-else-if="statuses.export === 'success'") Done!
                span.text-red-700.dark_text-red-400(v-else-if="statuses.export === 'error'") {{ errors.export }}

          fieldset.p-4.my-6.bg-gray-100.dark_bg-gray-800.bg-opacity-10.rounded-lg.prose.prose.dark_prose-invert
            h2 Import TSVs
            p.opacity-80 Upload a TSV file to update the translation for a course. After you choose some files, you can confirm the target language for each file and whether it was translated by AI or a human.

            p.opacity-80 When you finish importing translations, #[b Publish] below to make them live.

            p.opacity-80 Drag some files into the box or click it to browse: 

            p
              span.border-dashed.border-4.inline-block.border-gray-500.bg-gray-500.bg-opacity-20
                input.py-6.cursor-pointer.opacity-0(type="file" ref="fileInput" accept=".tsv" @change="handleFileChange" multiple) 
            //- radios for AI vs human

            div(v-if="files.length > 0")
              p.opacity-80 Please review our guesses for each file:

            ul
              li.flex.gap-8.items-center(v-for="(file, i) in files")
                span.flex-1 {{ file.name }}
                span.opacity-50 {{ (file.size / 1024).toFixed(1) }} KB
                fieldset.inline-flex.gap-3
                  //- radios for live vs internal
                  label.flex.gap-1.items-center
                    input(type="radio" v-model="fileConfigs[file.name].type" value="ai")
                    span AI
                    abbr.mx-0.-mt-2.no-underline.border-b.border-dotted.scale-75.text-xs.font-bold.opacity-50(title="Unreviewed machine translations, may be overwritten by new translations.") 
                      span [?]

                  label.flex.gap-1.items-center(v-if="firmCode")
                    input(type="radio" v-model="fileConfigs[file.name].type" value="untrusted")
                    span Untrusted
                    abbr.mx-0.-mt-2.no-underline.border-b.border-dotted.scale-75.text-xs.font-bold.opacity-50(title="Reviewed for a specific firm, not to be reused outside the firm.") 
                      span [?]

                  label.flex.gap-1.items-center(v-else="")
                    input(type="radio" v-model="fileConfigs[file.name].type" value="human")
                    span Human
                    abbr.mx-0.-mt-2.no-underline.border-b.border-dotted.scale-75.text-xs.font-bold.opacity-50(title="Reviewed and trusted for future reuse.") 
                      span [?]

                label
                  | to 
                  | 
                  input(
                    type="text" :value="fileConfigs[file.name].to" 
                    @input="event => fileConfigs[file.name].to = event.target?.value || ''" 
                    class="w-12 mx-1" :class="{ '!ring-red-500 !ring-2': uploadLangErrors[i] }"
                    )
                button.btn.text-xs.bg-red-500.text-white.py-1.px-3.rounded-full(type="button" @click="files.splice(files.indexOf(file), 1)") 
                  span.opacity-75 Remove

            p(v-if="files.length > 1")
              button.btn.bg-red-500.text-white.py-1.px-3.rounded-full(type="button" @click="files = []") 
                span.opacity-75 Remove all

            p
              //- file upload
              button.ms-auto.btn-hover_cursor-pointer.bg-primary-600.btn-hover_bg-primary-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(type="button" :disabled="files.length === 0 || uploadLangErrors.some(Boolean)" @click="uploadFile")  
                span.group-disabled_opacity-50 Upload

              span.px-4
                span.italic.opacity-50(v-if="files.length === 0") Please choose some TSVs to upload.
                span.italic.opacity-50(v-else-if="uploadLangErrors.some(Boolean)") Choose a valid language for each file.
                span.opacity-75(v-else-if="statuses.import === 'pending'") Working...
                span.opacity-75(v-else-if="statuses.import === 'success'") Done!
                span.text-red-700.dark_text-red-400(v-else-if="statuses.import === 'error'") {{ errors.import }}

          fieldset.p-4.my-6.bg-gray-100.dark_bg-gray-800.bg-opacity-10.rounded-lg.prose.prose.dark_prose-invert
            h2 Publish translations

            p
              LanguageSelect(v-model="langs")

            p.opacity-80 Publish translations to S3 so that they appear in the course, either live for learners or for internal review.

            .bg-yellow-800.bg-opacity-20.p-2.px-6(v-if="yaml?.includes('captions:')")
              p.font-bold
                span.text-2xl ⚠️
                span Video caption (VTT) files!
              p This course includes video captions.
              p Publishing translations also involves translating the VTT video captions and uploading them to the appropriate S3 bucket.
              p Please check the course history and republish the course YAML if the caption URLs were updated during the translation publishing process.

              .flex.gap-6.items-center.mt-4
              label.flex.gap-2.items-center.text-gray-900.dark_text-gray-100
                span VTT Translation Provider:
                select.rounded.px-2.py-1(v-model="translationProvider")
                  option.text-gray-900.dark_text-gray-100.bg-white.dark_bg-gray-900(value="azure") Azure
                  option.text-gray-900.dark_text-gray-100.bg-white.dark_bg-gray-900(value="openai") OpenAI

              .flex.gap-6.items-center.mt-4
                label.flex.gap-1.items-center
                  input(type="checkbox" v-model="refreshAiCaptions") 
                  |  
                  span Refresh existing AI caption translations
                  abbr.mx-0.no-underline.border-b.border-dotted.font-bold.opacity-50(title="Checking this will overwrite any existing AI caption translations. Leave unchecked to skip existing VTT files.")
                    span [?]

            p.opacity-80 Publishing to "live" publishes to "internal" as well, to keep the internal version up to date.

            fieldset.flex.gap-4
              //- radios for live vs internal
              label.flex.gap-1
                input(type="radio" v-model="publishTo" value="live")
                span Live

              label.flex.gap-1
                input(type="radio" v-model="publishTo" value="internal")
                span Internal
            p
              button.ms-auto.btn-hover_cursor-pointer.bg-primary-600.btn-hover_bg-primary-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(type="button" :disabled="!langs || !validLangs" @click="publish") 
                span.group-disabled_opacity-50  Publish

              span.px-4
                span.italic.opacity-50(v-if="!langs") Please choose some languages.
                span.italic.opacity-50(v-else-if="!validLangs") You chose an unknown/deprecated language.
                span.opacity-75(v-else-if="statuses.publish === 'pending'") Working...
                span.opacity-75(v-else-if="statuses.publish === 'success'") Done!
                span.text-red-700.dark_text-red-400(v-else-if="statuses.publish === 'error'") {{ errors.publish }}

            template(v-if="statuses.publish === 'success'")
              p.mt-10.mb-0
                span
                  a.text-blue-500.hover_underline(:href="`#/editor/${yamlFilename.slice(0, -4)}`") Edit {{ yamlFilename }}

              p
                span
                  a.text-blue-500.hover_underline(:href="`#/history/${yamlFilename.slice(0, -4)}`") History {{ yamlFilename }}

</template>

<script setup lang="ts">
import MainMenu from './MainMenu.vue';
import {
  exportTsv,
  getLatestSaved,
  uploadTsv
} from '../lib/callModContentAuthors';
import { formatTimeAgo } from '@vueuse/core';
import ToggleDark from './ToggleDark.vue';
import {
  createAiTranslations,
  publishTranslations
} from '../lib/callModContentAuthors';
import useDocTitle from '../lib/useDocTitle';
import { onMounted, ref, computed, reactive } from 'vue';
import PrettyDiff from './PrettyDiff.vue';
import LanguageSelect from './LanguageSelect.vue';
import languagesMap from '../../lib/languages.json';
import activeLanguages from '../../lib/activeLanguages.yml';
import TransitionExpand from './TransitionExpand.vue';
import extractBaseCourse from '../../lib/extractBaseCourse';
import expandVttUploadPath from '../../lib/expandVttUploadPath';
import findVttPathForLocale from '../../lib/findVttPathForLocale';
import jsYaml from 'js-yaml';
import JSZip from 'jszip';
import timestamp from '../../../esm/lib/time-stamp.js';
import { fetchContent } from '../../lib/fetchContent';
const props = defineProps<{ courseId: number; firmCode?: string }>();
const langs = ref('');
const courseAndFirm = { courseId: props.courseId, firmCode: props.firmCode };
const generateAi = ref(true);
const refreshAi = ref(false);
const refreshAiCaptions = ref(false);
const translationProvider = ref('azure');
const hasSavedData = ref(false);
import { loadCourse } from '../store/courses';

const files = ref<File[]>([]);
const fileInput = ref<HTMLInputElement>();
const shouldExportVideoCaptions = ref(false);
const shouldIncludeAllTranslations = ref(false);

declare module '../../lib/activeLanguages.yml' {
  const arr: string[];
  // @ts-ignore
  export default arr;
}

const languages = Object.keys(languagesMap)
  .filter(code => activeLanguages.includes(code))
  .filter(code => !languagesMap[code].deprecated)
  .map(code => ({
    code,
    ...languagesMap[code]
  }));

const deprecatedLanguages = Object.keys(languagesMap)
  .filter(code => languagesMap[code].deprecated)
  .map(code => ({
    code,
    ...languagesMap[code]
  }));

const validLangs = computed(() =>
  langs.value
    .trim()
    .split(/\s+/)
    .every(lang => languages.some(l => l.code === lang))
);

const validExportLangs = computed(() =>
  langs.value
    .trim()
    .split(/\s+/)
    .every(
      lang =>
        languages.some(l => l.code === lang) ||
        deprecatedLanguages.some(l => l.code === lang)
    )
);

const fileConfigs = ref<{
  [key: string]: {
    file: File;
    type: 'ai' | 'human' | 'untrusted';
    to: string;
  };
}>({});

const statuses = reactive<{
  [name: string]: 'idle' | 'pending' | 'success' | 'error';
}>({
  translate: 'idle',
  export: 'idle',
  import: 'idle',
  publish: 'idle'
});

const errors = reactive<{
  [name: string]: string;
}>({
  translate: '',
  export: '',
  import: '',
  publish: ''
});

const allSelected = computed(() => {
  // all selected languages are in the langs string
  const selected = langs.value.trim().split(/\s+/);
  // all languages in the courseLanguages array are selected
  const all = courseLanguages.value.every(l => selected.includes(l));
  return all;
});

const isDeprecated = lang => {
  return deprecatedLanguages.some(l => l.code === lang);
};

const deprecatedCount = computed(() => {
  return courseLanguages.value.filter(isDeprecated).length;
});

const updateLangs = event => {
  const { value: lang } = event.target as HTMLInputElement;
  if (!langs.value.includes(lang)) {
    langs.value = langs.value.trim() + ' ' + lang;
  } else {
    langs.value = langs.value
      .trim()
      .split(/\s+/)
      .filter(l => l !== lang)
      .join(' ');
  }
};

const selectAllLangs = event => {
  const { checked } = event.target as HTMLInputElement;
  if (checked) {
    // add all courseLanguages to langs and also keep the rest of the langs
    const selected = langs.value.trim().split(/\s+/);
    const all = courseLanguages.value.filter(l => !selected.includes(l));
    langs.value = langs.value.trim() + ' ' + all.join(' ');
  } else {
    // remove all courseLanguages from langs
    langs.value = langs.value
      .trim()
      .split(/\s+/)
      .filter(l => !courseLanguages.value.includes(l))
      .join(' ');
  }
};

const handleFileChange = () => {
  if (!fileInput.value) return;
  files.value = [...files.value, ...Array.from(fileInput.value.files || [])];

  const prevConfigs = fileConfigs.value;
  fileConfigs.value = files.value.reduce(
    (acc, file) => {
      const key = file.name;
      const includesAi = /\.ai.*\.tsv$/.test(file.name);
      const includesUntrusted = /\.untrusted.*\.tsv$/.test(file.name);
      const fileFirstName = file.name.split('.')[0].split(' ')[0];

      // guess target language from _es.tsv
      let to = fileFirstName.split('_').pop() || '';

      if (!languages.some(l => l.code === to)) {
        to = '';
      }

      acc[key] = prevConfigs[key] || {
        file,
        type: includesAi
          ? 'ai'
          : props.firmCode && includesUntrusted
            ? 'untrusted'
            : 'human',
        to
      };
      return acc;
    },
    {} as typeof fileConfigs.value
  );
};

const uploadLangErrors = computed(() =>
  files.value.map(file => {
    const c = fileConfigs.value[file.name];
    const to = c?.to || '';
    if (!to.trim()) return 'Please enter a language code.';
    if (!languages.some(l => l.code === to.trim()))
      return 'Invalid language code.';
    return '';
  })
);

const yamlFilename = computed(() =>
  props.firmCode
    ? `${props.firmCode}_${props.courseId}.yml`
    : `${props.courseId}.yml`
);

const title = ref<string>('');
const yaml = ref<string>('');
const diff = ref<string>('');
const baseDiff = ref<string>('');
const hasDiff = computed(() => diff.value.includes('@@'));
const hasBaseDiff = computed(() => baseDiff.value.includes('@@'));
const loadCourseError = ref<string>('');
const dryRunError = ref<string>('');
const lastUpdated = ref<Date>();
const publishTo = ref<'live' | 'internal'>('live');
const baseCourseId = ref<number>();
const baseCourseYamlFilename = computed(() => `${baseCourseId.value}.yml`);
const courseLanguages = ref([]);
const isReady = ref(false);

onMounted(async () => {
  let json: Content;
  isReady.value = false;

  try {
    const course = await loadCourse(props);
    if (!course) throw new Error('Course not found.');

    const { latest, latestDraft, hasUnsavedChanges } = course;
    if (!latest?.courseId) throw new Error('Course not found.');

    if (hasUnsavedChanges) {
      const draftAuthor = latestDraft?.author;
      throw new Error(`Course has unsaved changes from ${draftAuthor}.`);
    }

    const data = await getLatestSaved(courseAndFirm);

    if (!data) {
      throw new Error('Course not found.');
    }

    lastUpdated.value = new Date(+data.metadata.updatedDate);
    title.value = data.title ?? '';
    yaml.value = data.yaml ?? '';
    hasSavedData.value = data.metadata.updatedDate ? true : false;
    if (hasSavedData.value) {
      json = jsYaml.load(data.yaml) as Content;
    }
  } catch (e) {
    loadCourseError.value = e.message;
    return;
  }

  try {
    // if this is a customised course with partial/patches content, check that the base course is ready for translations first
    if (props.firmCode && !json?.pages) {
      baseCourseId.value = extractBaseCourse(yaml.value) ?? props.courseId;

      const dryRun = await createAiTranslations({
        ...{ courseId: baseCourseId.value, firmCode: '' },
        to: 'en',
        dryRun: true,
        provider: translationProvider.value
      }).catch(e => {
        throw new Error(
          `Base course translation error (${baseCourseId.value}): ${e.message}`
        );
      });

      if (dryRun) {
        const r = (dryRun?.data?.data?.results || '{}') as string;
        baseDiff.value = JSON.parse(r).diff as string;
      }
    }

    // get the current course languages from the yaml
    let courseLangs = json?.languages
      ? json.languages
      : json && json['default-language']
        ? [json['default-language']]
        : [];

    // remove any en-GB from the languages array (it shouldn't be in there)
    if (courseLangs.length > 0) {
      courseLanguages.value = courseLangs.filter(l => l !== 'en-GB').sort();
    }

    // if the base course is ok or there isn't one, check this course - but only if it has translatable content
    if (
      (!hasBaseDiff.value || !props.firmCode) &&
      (json?.course || json?.pages || json?.patches)
    ) {
      try {
        const dryRun = await createAiTranslations({
          ...courseAndFirm,
          to: 'en',
          dryRun: true,
          provider: translationProvider.value
        }).catch(e => {
          throw new Error(`Course translation error: ${e.message}`);
        });

        if (dryRun) {
          const r = (dryRun?.data?.data?.results || '{}') as string;
          diff.value = JSON.parse(r).diff as string;
        }

        isReady.value = true;
      } catch (e) {
        dryRunError.value = e.message;
        return;
      }
    } else {
      isReady.value = true;
    }
  } catch (e) {
    loadCourseError.value = e.message;
  }
});

/**
 * Wraps an async task in status and error handling.
 * @param {string} name
 * @param {() => Promise<void>} task
 */
const handleTaskStatus = async (name, task) => {
  statuses[name] = 'pending';
  errors[name] = '';

  try {
    await task();
    statuses[name] = 'success';
    errors[name] = '';
  } catch (e) {
    statuses[name] = 'error';
    errors[name] = '🚨 ' + e.message;
  }
};

const translate = async () => {
  await handleTaskStatus('translate', async () => {
    for (const to of langs.value.trim().split(/\s+/)) {
      const params = {
        ...courseAndFirm,
        to,
        refreshAi: refreshAi.value,
        skipAi: !generateAi.value,
        provider: translationProvider.value
      };
      await createAiTranslations(params);
    }
  });

  diff.value = '';
};

const publish = async () => {
  await handleTaskStatus('publish', async () => {
    for (const to of langs.value.trim().split(/\s+/)) {
      const params = {
        ...courseAndFirm,
        to,
        provider: translationProvider.value,
        translateExistingCaptions: refreshAiCaptions.value,
        publishTo: publishTo.value
      };
      await publishTranslations(params);

      // "live" translations are also published to "internal"
      if (publishTo.value === 'live')
        await publishTranslations({
          ...courseAndFirm,
          to,
          provider: translationProvider.value,
          translateExistingCaptions: refreshAiCaptions.value,
          publishTo: 'internal'
        });
    }
  });
};

const download = async () => {
  await handleTaskStatus('export', async () => {
    const dts = timestamp('YYYY-MM-DD-HHmmss-ms');

    // should we prompt to download a single TSV file or collate multiple in to a zip file?
    const shouldDownloadTsv =
      langs.value.trim().split(/\s+/).length === 1 &&
      !shouldExportVideoCaptions.value;

    // create a new zip file for the tsv and captions files
    const zip = !shouldDownloadTsv ? new JSZip() : null;

    for (const to of langs.value.trim().split(/\s+/)) {
      const { filename, tsv, typeCounts } = await exportTsv({
        ...courseAndFirm,
        to,
        download: shouldDownloadTsv,
        suffix: shouldDownloadTsv ? dts : '',
        includeAll: shouldIncludeAllTranslations.value
      });

      console.log(
        `Exported translations (${to}): ${JSON.stringify(typeCounts)}`
      );

      // add each tsv files to the zip
      if (!shouldDownloadTsv && zip) {
        zip.file(`translations/${filename}`, tsv);
      }
    }

    if (shouldExportVideoCaptions.value && zip) {
      // add the VTT files to the same zip
      await exportVideoCaptions(zip);
    }

    if (!shouldDownloadTsv && zip) {
      // download the zip file
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const filename = `${yamlFilename.value.replace(
        '.yml',
        `_translations_export_${dts}.zip`
      )}`;
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  });
};

const uploadFile = async () => {
  await handleTaskStatus('import', async () => {
    for (const [, { file, type, to }] of Object.entries(fileConfigs.value)) {
      await uploadTsv({ ...courseAndFirm, to, file, type });
    }
  });
};

// get all of the VTT files from the course json and download them in a zip file
const exportVideoCaptions = async (existingZip: JSZip) => {
  const zip = existingZip || new JSZip();
  const json = jsYaml.load(yaml.value) as Content;

  // Function to recursively find all VTT URLs in the course content
  const findVttUrls = async (obj: any) => {
    const urls: { [key: string]: string } = {};

    if (typeof obj !== 'object' || obj === null) return urls;

    // Check for VTT URLs in the captions property
    if (
      obj.captions &&
      typeof obj.captions === 'string' &&
      obj.captions.endsWith('.vtt')
    ) {
      const baseUrl = obj.captions;
      const baseFilename =
        baseUrl.split('/').pop() || `${obj.id || 'unknown'}.vtt`;

      // Add the base VTT file
      urls[`captions/${baseFilename}`] = baseUrl;

      // Check for translated versions of the VTT file
      if (langs.value) {
        for (const lang of langs.value.trim().split(/\s+/)) {
          if (lang === 'en-GB') continue; // Skip en-GB as it's the default language

          // Try to find translated version by replacing the extension with language code
          const translatedUrl = await findVttPathForLocale(baseUrl, lang, {
            useRegex: true
          });
          const translatedFilename =
            translatedUrl?.split('/').pop() || baseFilename;
          urls[`captions/${translatedFilename}`] = translatedUrl;
        }
      }
    }

    // Recursively search through all object properties
    for (const value of Object.values(obj)) {
      if (typeof value === 'object' && value !== null) {
        const nestedUrls = await findVttUrls(value);
        Object.assign(urls, nestedUrls);
      }
    }

    return urls;
  };

  const allVttUrls = await findVttUrls(json);

  // Download each VTT file and add it to the zip
  for (const [filename, url] of Object.entries(allVttUrls)) {
    try {
      const expandedUrl = expandVttUploadPath(url);
      const content = await fetchContent(expandedUrl).catch(() => {
        console.warn(`Failed to download ${expandedUrl}`);
        return null;
      });

      if (content) {
        zip.file(filename, content);
      }
    } catch (error) {
      console.warn(`Error downloading ${expandedUrl}:`, error);
    }
  }

  return zip;
};

const docTitle = computed(() => `Translate ${yamlFilename.value}`);

useDocTitle(docTitle);
</script>

<style scoped>
input[type='file']::file-selector-button {
  background-color: transparent;
  color: currentColor;
  border: 0px;
  border-right: 1px solid #e5e5e5;
  padding: 10px 15px;
  margin-right: 20px;
  transition: 0.5s;
}

/* input[type=file]::file-selector-button:hover {
  background-color: #eee;
  border: 0px;
  border-right: 1px solid #e5e5e5;
} */
</style>
